<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <edit-employee-layout :employee-id="empNumber" screen="default">
    <div class="orangehrm-horizontal-padding orangehrm-vertical-padding">
      <oxd-text tag="h6" class="orangehrm-main-title">
        Assigned Assets
      </oxd-text>
    </div>
    <table-header
      :selected="checkedItems.length"
      :loading="isLoading"
      :total="total"
    ></table-header>
    <div class="orangehrm-container">
      <oxd-card-table
        v-model:selected="checkedItems"
        v-model:order="sortDefinition"
        :headers="headers"
        :items="items?.data"
        :clickable="false"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
        :selectable="$can.delete(`time_projects`)"
      />
    </div>
    <div class="orangehrm-bottom-container">
      <oxd-pagination
        v-if="showPaginator"
        v-model:current="currentPage"
        :length="pages"
      />
    </div>
  </edit-employee-layout>
</template>

<script>
import {computed, ref} from 'vue';
import {validSelection} from '@/core/util/validation/rules';
import useSort from '@ohrm/core/util/composable/useSort';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import EditEmployeeLayout from '@/orangehrmPimPlugin/components/EditEmployeeLayout';

const defaultFilters = {
  employee: null,
  assetCategory: null,
  assetBrand: null,
  assetVendor: null,
  serialNumber: null,
};

const defaultSortOrder = {
  'project.name': 'ASC',
  'customer.name': 'DEFAULT',
  'projectCategory.name': 'DEFAULT',
  'employee.lastName': 'DEFAULT',
};

export default {
  components: {
    'edit-employee-layout': EditEmployeeLayout,
  },
  props: {
    empNumber: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const {$tEmpName} = useEmployeeNameTranslate();
    const projectNormalizer = (data) => {
      return data.map((item) => {
        return {
          id: item.id,
          category: item.assetCategory?.name,
          serialNumber: item.serialNumber,
          brand: item.assetBrand?.name,
          model: item.model,
          description: item.description,
          warrantyStart: item.warrantyStart,
          warrantyEnd: item.warrantyEnd,
          acquired: item.acquired,
          employee: item.employee?.empNumber
            ? $tEmpName(item.employee, {includeMiddle: false})
            : '',
          assignedFrom: item.assignedFrom,
          status: item.status,
        };
      });
    };

    const filters = ref({...defaultFilters});
    const {sortDefinition, onSort} = useSort({
      sortDefinition: defaultSortOrder,
    });

    const serializedFilters = computed(() => {
      return {
        assetCategory: filters.value.assetCategory?.id,
        assetBrand: filters.value.assetBrand?.id,
        assetVendor: filters.value.assetVendor?.id,
        empNumber: props.empNumber,
        serialNumber: filters.value?.serialNumber,
      };
    });

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/admin/assets',
    );

    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: projectNormalizer,
    });

    onSort(execQuery);

    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      filters,
      sortDefinition,
    };
  },
  data() {
    return {
      checkedItems: [],
      rules: {
        assetCategory: [validSelection],
      },
    };
  },
  computed: {
    headers() {
      const headers = [
        {
          name: 'category',
          title: 'Category',
          style: {flex: '10%'},
        },
        {
          name: 'serialNumber',
          title: 'Serial Number',
          style: {flex: '10%'},
        },
        {
          name: 'brand',
          title: 'Brand',
          style: {flex: '10%'},
        },
        {
          name: 'model',
          title: 'Model',
          style: {flex: '10%'},
        },
        {
          name: 'status',
          title: 'Status',
          style: {flex: '10%'},
        },
        {
          name: 'employee',
          title: 'Assigned Employee',
          style: {flex: '10%'},
        },
        {
          name: 'assignedFrom',
          title: 'Assigned On',
          style: {flex: '10%'},
        },
        {
          name: 'warrantyEnd',
          title: 'Warranty End',
          style: {flex: '10%'},
        },
      ];

      return headers;
    },
  },
  methods: {
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    async filterItems() {
      await this.execQuery();
    },
  },
};
</script>
